import { createBraze, type BrazeClient, type SubscriptionState } from '@/services/braze/index';

let clientPromise: Promise<BrazeClient> | null = null;
let started = false;

export async function startBrazeIfConsented(consented: boolean) {
    if (!consented || started) return;
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    await client.start(process.env.VUE_APP_BRAZE_API_KEY || '', process.env.VUE_APP_BRAZE_BASE_URL || '', {
        serviceWorker: '/braze-service-worker.js',
        logging: process.env.NODE_ENV === 'development',
    });
    started = true;
}

export async function identifyBrazeUser(externalId: string, email?: string, subs?: { push?: SubscriptionState; email?: SubscriptionState }) {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    client.changeUser(externalId);
    if (email) client.setEmail(email.trim().toLowerCase());
    if (subs) client.setSubscriptions(subs);
}

// Attach email + subscription state to the current (anonymous) Braze user
// without calling changeUser. When the same-device guest later signs in,
// Braze's SDK merges this anonymous profile into the identified one, so
// no duplicate is created. Use for guest marketing opt-ins.
export async function setBrazeEmailAndSubscriptions(email: string, subs?: { push?: SubscriptionState; email?: SubscriptionState }) {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    if (email) client.setEmail(email.trim().toLowerCase());
    if (subs) client.setSubscriptions(subs);
}

// Attach a user_alias to the current Braze profile. Used on guest opt-in
// so the server can later call /users/identify to merge the anonymous
// profile into the identified customer (covers cross-device guest→signup).
export async function addBrazeAlias(alias: string, label: string) {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    client.addAlias(alias, label);
}

export async function trackBrazeEvent(name: string, props?: Record<string, unknown>) {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    client.logEvent(name, props);
}

export async function refreshContentCards(): Promise<void> {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    await client.requestContentCardsRefresh();
}

export async function getContentCards() {
    if (!clientPromise) clientPromise = createBraze();
    const client = await clientPromise;
    return client.getContentCards();
}
